import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, UserPlus, Trash, Key, Copy, CheckCircle, Warning, Buildings, UploadSimple, PencilSimple, Phone, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Administrateur", acquereur: "Acquéreur", analyste: "Analyste M&A",
  associe: "Associé", investisseur: "Investisseur (lecture)", sales_aeliam: "Sales Aeliam",
};

function BrandingSection() {
  const [name, setName] = useState("");
  const [branding, setBranding] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get("/settings/branding").then((r) => { setBranding(r.data); setName(r.data.acquereur_name || ""); });
  useEffect(() => { load(); }, []);

  const saveName = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.put("/settings/branding", { acquereur_name: name.trim() }); toast.success("Nom enregistré"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo mis à jour");
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Échec de l'upload"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="branding-section">
      <div className="flex items-center gap-2 mb-1">
        <Buildings size={18} className="text-primary" weight="fill" />
        <h2 className="font-head font-bold text-base">Identité de marque</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Personnalisez le nom de l'acquéreur et le logo affichés dans vos emails et documents PDF (LOI, NDA, rapports).
      </p>

      <div className="space-y-5">
        <div className="max-w-md">
          <label className="overline text-muted-foreground">Nom de l'acquéreur (expéditeur)</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="branding-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="AI Assurances"
              className="flex-1 px-3 py-2 text-sm border border-border outline-none focus:border-primary" />
            <button data-testid="branding-name-save" onClick={saveName} disabled={busy || !name.trim()}
              className="px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">Enregistrer</button>
          </div>
        </div>

        <div>
          <label className="overline text-muted-foreground">Logo</label>
          <div className="flex items-center gap-4 mt-2">
            <div className="w-40 h-20 border border-border bg-secondary flex items-center justify-center overflow-hidden" data-testid="branding-logo-preview">
              {branding?.logo_url ? (
                <img src={`${backend}${branding.logo_url}`} alt="logo" className="max-h-16 max-w-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">Aucun logo</span>
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="hidden" data-testid="branding-logo-file" />
              <button data-testid="branding-logo-upload" onClick={() => fileRef.current?.click()} disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-primary text-primary hover:bg-blue-50 disabled:opacity-50">
                <UploadSimple size={16} /> {branding?.has_logo ? "Remplacer le logo" : "Téléverser un logo"}
              </button>
              <p className="text-xs text-muted-foreground mt-1.5">PNG, JPG ou WebP. Idéalement sur fond transparent.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function MfaSection() {
  const { user, refreshUser } = useAuth();
  const [enroll, setEnroll] = useState(null); // {qr, uri}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/enroll/start"); setEnroll({ qr: data.qr_png_base64, uri: data.otpauth_uri }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/confirm", { code });
      setRecovery(data.recovery_codes); setEnroll(null); setCode("");
      await refreshUser();
      toast.success("MFA activé !");
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide"); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/auth/mfa/disable", { password: pwd }); setPwd(""); await refreshUser(); toast.success("MFA désactivé"); }
    catch (e) { toast.error(e.response?.data?.detail || "Mot de passe incorrect"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="mfa-section">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-primary" weight="fill" />
        <h2 className="font-head font-bold text-base">Double authentification (2FA)</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Sécurisez votre compte avec une application d'authentification (Google Authenticator, Authy…).
      </p>

      {user?.mfa_enabled ? (
        <div data-testid="mfa-enabled-block">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full mb-4">
            <CheckCircle size={16} weight="fill" /> 2FA activée
          </div>
          <div className="max-w-xs">
            <label className="overline text-muted-foreground">Désactiver (confirmez votre mot de passe)</label>
            <input data-testid="mfa-disable-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
              className="w-full mt-1 mb-2 px-3 py-2 text-sm border border-border outline-none focus:border-primary" />
            <button data-testid="mfa-disable-btn" onClick={disable} disabled={busy || !pwd}
              className="px-4 py-2 text-sm font-semibold border border-destructive text-destructive hover:bg-red-50 disabled:opacity-50">
              Désactiver la 2FA
            </button>
          </div>
        </div>
      ) : recovery ? (
        <div data-testid="recovery-codes-block" className="border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-2"><Warning size={16} weight="fill" /> Conservez vos codes de récupération</div>
          <p className="text-xs text-amber-700 mb-3">Chaque code n'est utilisable qu'une fois si vous perdez l'accès à votre application. Ils ne seront plus affichés.</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-3">
            {recovery.map((c) => <div key={c} className="bg-white border border-amber-200 px-2 py-1 rounded text-center">{c}</div>)}
          </div>
          <button onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Codes copiés"); }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            <Copy size={14} /> Copier les codes
          </button>
        </div>
      ) : enroll ? (
        <div data-testid="mfa-enroll-block" className="flex flex-col sm:flex-row gap-6">
          <img src={enroll.qr} alt="QR 2FA" className="w-44 h-44 border border-border" data-testid="mfa-qr" />
          <div className="flex-1">
            <p className="text-sm mb-2">1. Scannez ce QR code avec votre application d'authentification.</p>
            <p className="text-xs text-muted-foreground mb-3 break-all">Clé manuelle : <span className="font-mono">{enroll.uri.split("secret=")[1]?.split("&")[0]}</span></p>
            <label className="overline text-muted-foreground">2. Saisissez le code à 6 chiffres</label>
            <input data-testid="mfa-confirm-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
              className="w-full max-w-[200px] mt-1 mb-2 px-3 py-2 text-sm border border-border outline-none focus:border-primary font-mono tracking-widest" />
            <div>
              <button data-testid="mfa-confirm-btn" onClick={confirm} disabled={busy || code.length < 6}
                className="px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">Activer</button>
            </div>
          </div>
        </div>
      ) : (
        <button data-testid="mfa-start-btn" onClick={start} disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
          <Key size={16} /> Activer la 2FA
        </button>
      )}
    </div>
  );
}

function UsersSection() {
  const { user, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "investisseur", password: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/users", form);
      toast.success("Utilisateur créé");
      setForm({ name: "", email: "", role: "investisseur", password: "" });
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const remove = async (uid) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try { await api.delete(`/auth/users/${uid}`); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Erreur"); }
  };
  const changeRole = async (uid, role) => {
    try { await api.patch(`/auth/users/${uid}`, { role }); toast.success("Rôle mis à jour"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Erreur"); }
  };
  const resetPassword = async (uid, name) => {
    const pwd = window.prompt(`Nouveau mot de passe pour ${name} :`);
    if (!pwd) return;
    try { await api.patch(`/auth/users/${uid}`, { password: pwd }); toast.success("Mot de passe réinitialisé"); }
    catch (er) { toast.error(er.response?.data?.detail || "Erreur"); }
  };
  const changeName = async (uid, current) => {
    const name = window.prompt("Nom et prénom :", current || "");
    if (name === null) return;
    if (!name.trim()) { toast.error("Le nom ne peut pas être vide"); return; }
    try { await api.patch(`/auth/users/${uid}`, { name: name.trim() }); toast.success("Nom mis à jour"); load(); if (uid === user?.id) await refreshUser(); }
    catch (er) { toast.error(er.response?.data?.detail || "Erreur"); }
  };

  const isAdmin = user?.role === "super_admin";

  return (
    <div className="bg-white border border-border p-6" data-testid="users-section">
      <div className="flex items-center gap-2 mb-1">
        <UserPlus size={18} className="text-primary" weight="fill" />
        <h2 className="font-head font-bold text-base">Membres & investisseurs</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Gérez les accès. Les investisseurs ne voient que les documents qui leur sont partagés.</p>

      {isAdmin && (
        <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 mb-6 pb-6 border-b border-border" data-testid="create-user-form">
          <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet" required
            className="px-3 py-2 text-sm border border-border outline-none focus:border-primary" />
          <input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" required
            className="px-3 py-2 text-sm border border-border outline-none focus:border-primary" />
          <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="px-3 py-2 text-sm border border-border bg-white outline-none focus:border-primary">
            {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe initial" required
            className="px-3 py-2 text-sm border border-border outline-none focus:border-primary font-mono" />
          <button data-testid="create-user-btn" type="submit" disabled={busy}
            className="sm:col-span-2 py-2 bg-primary text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-50">
            {busy ? "…" : "Créer l'utilisateur"}
          </button>
        </form>
      )}

      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{u.name} {u.id === user?.id && <span className="text-xs text-muted-foreground">(vous)</span>}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
            </div>
            {u.mfa_enabled && <ShieldCheck size={15} className="text-emerald-600" weight="fill" title="2FA activée" />}
            {isAdmin && u.id !== user?.id ? (
              <>
                <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                  className="text-[11px] border border-border bg-white px-1.5 py-1 rounded outline-none focus:border-primary">
                  {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button data-testid={`edit-name-${u.id}`} onClick={() => changeName(u.id, u.name)} title="Modifier le nom"
                  className="text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
                <button data-testid={`reset-pwd-${u.id}`} onClick={() => resetPassword(u.id, u.name)} title="Réinitialiser le mot de passe"
                  className="text-muted-foreground hover:text-primary"><Key size={15} /></button>
                <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
              </>
            ) : (
              <>
                {isAdmin && u.id === user?.id && (
                  <button data-testid={`edit-name-${u.id}`} onClick={() => changeName(u.id, u.name)} title="Modifier mon nom"
                    className="text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
                )}
                <span className="text-[10px] uppercase tracking-wide border border-border bg-secondary px-2 py-0.5 rounded-full">{ROLE_LABELS[u.role] || u.role}</span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ObjectionsSection() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/call-objections").then((r) => setItems(r.data.items || [])).catch(() => {}); }, []);
  const upd = (i, k, v) => setItems((s) => s.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const add = () => setItems((s) => [...s, { objection: "", reponse: "" }]);
  const remove = (i) => setItems((s) => s.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/call-objections", { items: items.filter((o) => o.objection.trim()) });
      setItems(data.items || []);
      toast.success("Objections enregistrées");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-border p-5" data-testid="objections-section">
      <div className="flex items-center gap-2 mb-1">
        <Phone size={16} className="text-primary" weight="fill" />
        <span className="font-head font-bold">Objections d'appel personnalisées</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Vos objections/réponses récurrentes. L'IA les réutilisera <b>en priorité</b> dans chaque script d'appel généré.
      </p>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Aucune objection personnalisée. Ajoutez-en une ci-dessous.</p>}
        {items.map((o, i) => (
          <div key={i} className="border border-border p-3 space-y-2" data-testid={`objection-row-${i}`}>
            <div className="flex items-center gap-2">
              <input value={o.objection} onChange={(e) => upd(i, "objection", e.target.value)} placeholder="Objection (ex. « Je suis trop petit »)"
                data-testid={`objection-text-${i}`} className="flex-1 border border-border px-2 py-1.5 text-sm outline-none font-medium" />
              <button onClick={() => remove(i)} data-testid={`objection-remove-${i}`} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
            </div>
            <textarea value={o.reponse} onChange={(e) => upd(i, "reponse", e.target.value)} rows={2} placeholder="Votre réponse recommandée…"
              data-testid={`objection-response-${i}`} className="w-full border border-border px-2 py-1.5 text-sm outline-none" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={add} data-testid="objection-add" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-border hover:bg-secondary">
          <Plus size={14} /> Ajouter une objection
        </button>
        <button onClick={save} disabled={saving} data-testid="objections-save" className="px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

function RdvSlotsSection() {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [best, setBest] = useState(null);
  useEffect(() => {
    api.get("/settings/rdv-slots").then((r) => setText(r.data.text || "")).catch(() => {});
    api.get("/reports/best-slot").then((r) => setBest(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/rdv-slots", { text }); setText(data.text || ""); toast.success("Disponibilités enregistrées"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-border p-5" data-testid="rdv-slots-section">
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle size={16} className="text-primary" weight="fill" />
        <span className="font-head font-bold">Disponibilités pour RDV</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Collez vos créneaux habituels. L'IA les proposera dans les <b>scripts d'appel</b> et les <b>emails</b> (créneaux concrets).
      </p>
      {best?.enough_data && (
        <div className="flex items-center justify-between gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 mb-3" data-testid="best-slot-hint">
          <span>💡 Créneau le plus souvent accepté : <b>{best.label}</b> ({best.accept_share}% de vos {best.count} RDV datés)</span>
          <button data-testid="best-slot-insert" onClick={() => setText((t) => (t ? t + " · " : "") + best.label)}
            className="shrink-0 font-semibold text-emerald-700 hover:underline">Insérer</button>
        </div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={500}
        placeholder="Ex. : Mardi et jeudi 9h–12h · Visio possible en fin de journée · Éviter les lundis"
        data-testid="rdv-slots-input" className="w-full border border-border px-2 py-1.5 text-sm outline-none" />
      <button onClick={save} disabled={saving} data-testid="rdv-slots-save"
        className="mt-3 px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
        {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  return (
    <div>
      <header className="h-16 px-8 flex items-center border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl flex items-center gap-2"><ShieldCheck size={20} className="text-primary" /> Profil & Sécurité</h1>
      </header>
      <div className="p-8 max-w-3xl space-y-6">
        {user?.role === "super_admin" && <BrandingSection />}
        {user?.role === "super_admin" && <ObjectionsSection />}
        {user?.role === "super_admin" && <RdvSlotsSection />}
        <MfaSection />
        {user?.role === "super_admin" && <UsersSection />}
      </div>
    </div>
  );
}
